import type { AnalyticsStore } from '@stores/analytics.store';
import {
  type EtlJob,
  type EtlJobData,
  type JobExecution,
  JobExecutionStatus,
  type JobFrequency,
  type JobNextExecution,
  type JobStatus,
  type JobType
} from '@stratumn/jobs';
import { computed, makeObservable, observable } from 'mobx';

import Model, { type ModelError } from './base/base.model';

export class AnalyticsModel extends Model {
  name: string;
  data: EtlJobData;
  createdBy: string;
  frequency: JobFrequency;
  maxRetry: number;
  status: JobStatus;
  type: JobType;
  jobExecutions: JobExecution[];
  jobNextExecution?: JobNextExecution;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;

  constructor(store: AnalyticsStore, analytics: EtlJob, loading?: boolean) {
    super(store, analytics.id, loading);
    this.name = analytics.name;
    this.data = analytics.data;
    this.frequency = analytics.frequency;
    this.maxRetry = analytics.maxRetry;
    this.type = analytics.type;
    this.status = analytics.status;
    this.jobExecutions = analytics.jobExecutions ?? [];
    this.jobNextExecution = analytics.jobNextExecution ?? undefined;
    this.createdBy = analytics.createdBy;
    this.createdAt = analytics.createdAt;
    this.updatedAt = analytics.updatedAt;
    this.deletedAt = analytics.deletedAt;

    makeObservable<
      AnalyticsModel,
      | 'name'
      | 'status'
      | 'createdAt'
      | 'updatedAt'
      | 'deletedAt'
      | 'frequency'
      | 'maxRetry'
      | 'type'
      | 'data'
      | 'createdBy'
      | 'jobExecutions'
      | 'jobNextExecution'
    >(this, {
      name: observable,
      data: observable,
      createdBy: observable,
      frequency: observable,
      maxRetry: observable,
      type: observable,
      status: observable,
      createdAt: observable,
      updatedAt: observable,
      deletedAt: observable,
      jobExecutions: observable,
      jobNextExecution: observable,
      shouldBePolled: computed,
      areAllJobExecutionsFinished: computed
    });
  }

  /* ---------------------------- Computed getters ---------------------------- */

  get shouldBePolled(): boolean {
    return this.jobExecutions.some(
      jobExecution => jobExecution.status === JobExecutionStatus.RUNNING
    );
  }

  get areAllJobExecutionsFinished() {
    return this.jobExecutions.every(
      jobExecution =>
        jobExecution.status === JobExecutionStatus.COMPLETED ||
        jobExecution.status === JobExecutionStatus.FAILED
    );
  }

  /* ------------------------ Computed setters ------------------------ */
  setJobExecutions(jobExecutions: JobExecution[]): void {
    this.jobExecutions = jobExecutions;
  }

  /* ------------------------- Abstract class methods ------------------------- */
  get toJSON() {
    return {};
  }

  get isDeletable(): boolean {
    return false;
  }

  get errors(): ModelError[] {
    return [];
  }
}
